import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useHousehold } from '@/context/HouseholdContext'

// Troca a moeda principal (base) do household e recalcula TODAS as transações
// usando taxas históricas de cada data (regra do requisito central).
// Passos:
//  1) grava a nova base em households.base_currency
//  2) sync-rates (garante taxas em cache p/ os pares da nova base)
//  3) fx_recalc_household (reconverte amount_base de tudo com taxas históricas)
export function useUpdateBaseCurrency() {
  const { householdId, refresh } = useHousehold()
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (newBase: string) => {
      const { error: upErr } = await supabase
        .from('households')
        .update({ base_currency: newBase })
        .eq('id', householdId)
      if (upErr) throw upErr

      // garante taxas em cache p/ os novos pares (não bloqueia em caso de falha)
      try {
        await supabase.functions.invoke('sync-rates', { body: { mode: 'daily' } })
      } catch (e) {
        console.warn('[sync-rates] falhou ao trocar base:', e)
      }

      // recalcula amount_base de todas as transações com taxas históricas
      const { error: rpcErr } = await supabase.rpc('fx_recalc_household', { p_household_id: householdId })
      if (rpcErr) throw rpcErr
    },
    onSuccess: async () => {
      await refresh()
      void qc.invalidateQueries({ queryKey: ['transactions'] })
      void qc.invalidateQueries({ queryKey: ['balances'] })
      void qc.invalidateQueries({ queryKey: ['cardMonthFatura'] })
      void qc.invalidateQueries({ queryKey: ['cardTotalDebt'] })
    },
  })
}

// Grava a lista de moedas HABILITADAS do household (subconjunto do catálogo).
export function useUpdateEnabledCurrencies() {
  const { householdId, refresh } = useHousehold()
  return useMutation({
    mutationFn: async (codes: string[]) => {
      const { error } = await supabase.from('households').update({ enabled_currencies: codes }).eq('id', householdId)
      if (error) throw error
    },
    onSuccess: () => refresh(),
  })
}

// Moedas que JÁ têm dados (contas ou transações) — não podem ser desabilitadas.
export function useUsedCurrencies() {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['usedCurrencies', householdId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('household_currencies_in_use', { p_household: householdId })
      if (error) throw error
      return (data as string[] | null) ?? []
    },
  })
}
